// src/Dashboard.js - Dashboard moderne avec métriques API
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import StatCard from './components/StatCard';
import TrendChart from './components/TrendChart';
import ApiMetrics from './components/ApiMetrics';
import NewsWidget from './components/NewsWidget';
import './styles/Dashboard.css';

const Dashboard = () => {
  const [metalsData, setMetalsData] = useState({
    gold: { current: 0, change: 0, trend: [] },
    silver: { current: 0, change: 0, trend: [] },
    platinum: { current: 0, change: 0, trend: [] },
    palladium: { current: 0, change: 0, trend: [] },
  });
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const navigate = useNavigate();

  // État pour l'export CSV
  const [csvExport, setCsvExport] = useState({
    symbol: 'XAU',
    startDate: '',
    endDate: '',
    range: 'daily',
    isExporting: false
  });

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => {
      loadDashboardData();
      setLastUpdate(new Date());
    }, 30000); // Refresh toutes les 30s
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Charger les données des métaux
      const metalsResponse = await axios.get('/api/metals/live?metalId=all', { headers });
      const data = metalsResponse.data;
      
      // L'API retourne: { updatedAt: "...", metals: { XAU: {...}, XAG: {...}, ... } }
      const metals = data.metals || {};
      
      // Conversion oz troy vers kg (1 oz troy = 31.1035 grammes = 0.0311035 kg)
      const OZ_TO_KG = 1000 / 31.1035; // Pour convertir €/oz en €/kg

      // Générer des données de tendance simulées (à remplacer par de vraies données historiques)
      const generateTrend = (baseValue) => {
        return Array.from({ length: 20 }, (_, i) => {
          const variation = Math.sin(i / 3) * (baseValue * 0.015);
          return baseValue + variation + (Math.random() - 0.5) * (baseValue * 0.005);
        });
      };

      // Calculer le changement de prix (simulé)
      const calculateChange = () => ((Math.random() - 0.5) * 3).toFixed(2);

      // XAU = Or, XAG = Argent, XPT = Platine, XPD = Palladium
      const goldPrice = metals.XAU?.mid || 0;
      const silverPrice = metals.XAG?.mid || 0;
      const platinumPrice = metals.XPT?.mid || 0;
      const palladiumPrice = metals.XPD?.mid || 0;

      setMetalsData({
        gold: {
          current: goldPrice,
          currentKg: goldPrice * OZ_TO_KG,
          change: calculateChange(),
          trend: generateTrend(goldPrice || 2000),
        },
        silver: {
          current: silverPrice,
          currentKg: silverPrice * OZ_TO_KG,
          change: calculateChange(),
          trend: generateTrend(silverPrice || 25),
        },
        platinum: {
          current: platinumPrice,
          currentKg: platinumPrice * OZ_TO_KG,
          change: calculateChange(),
          trend: generateTrend(platinumPrice || 1000),
        },
        palladium: {
          current: palladiumPrice,
          currentKg: palladiumPrice * OZ_TO_KG,
          change: calculateChange(),
          trend: generateTrend(palladiumPrice || 1800),
        },
      });

      // Charger les mises à jour récentes
      setRecentUpdates([
        {
          id: 1,
          metal: 'Or',
          price: goldPrice,
          time: 'Il y a 2 min',
          status: parseFloat(calculateChange()) >= 0 ? 'up' : 'down',
        },
        {
          id: 2,
          metal: 'Argent',
          price: silverPrice,
          time: 'Il y a 5 min',
          status: parseFloat(calculateChange()) >= 0 ? 'up' : 'down',
        },
        {
          id: 3,
          metal: 'Platine',
          price: platinumPrice,
          time: 'Il y a 8 min',
          status: parseFloat(calculateChange()) >= 0 ? 'up' : 'down',
        },
        {
          id: 4,
          metal: 'Palladium',
          price: palladiumPrice,
          time: 'Il y a 12 min',
          status: parseFloat(calculateChange()) >= 0 ? 'up' : 'down',
        },
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    }
  };

  const formatLastUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    if (diff < 60) return `Il y a ${diff}s`;
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)}min`;
    return lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Handler pour l'export CSV
  const handleExportCSV = async () => {
    const { symbol, startDate, endDate, range } = csvExport;

    // Validation
    if (!startDate || !endDate) {
      alert('Veuillez sélectionner une date de début et une date de fin');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('La date de début doit être antérieure à la date de fin');
      return;
    }

    try {
      setCsvExport(prev => ({ ...prev, isExporting: true }));

      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        symbol,
        start: startDate,
        end: endDate,
        range
      });

      const response = await fetch(`/api/metals/export-csv?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de l\'export');
      }

      // Télécharger le fichier
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metal_${symbol.toLowerCase()}_${startDate}_${endDate}_${range}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Export CSV réussi !');
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setCsvExport(prev => ({ ...prev, isExporting: false }));
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner-modern">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        <p className="loading-text">Chargement des données...</p>
      </div>
    );
  }

  return (
    <div className="dashboard modern-dashboard">
      <div className="page-container">
        {/* Header avec gradient */}
        <div className="page-header glass-header">
          <div className="header-content">
            <div className="header-text">
              <h1 className="page-title">
                <span className="title-icon">💎</span>
                Tableau de bord
              </h1>
              <p className="page-subtitle">
                Vue d'ensemble des cours des métaux précieux en temps réel
                <span className="update-badge">{formatLastUpdate()}</span>
              </p>
            </div>
            <div className="header-actions">
              <button className="btn btn-outline modern-btn" onClick={() => navigate('/charts')}>
                <span className="btn-icon">📈</span>
                Graphiques
              </button>
              <button className="btn btn-primary modern-btn" onClick={loadDashboardData}>
                <span className="btn-icon">🔄</span>
                Actualisers
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid - Métaux */}
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">🥇</span>
              Cours des métaux précieux
            </h2>
            <span className="section-badge live">● En direct</span>
          </div>
          <div className="stats-grid">
            <StatCard
              title="Or (Gold)"
              value={metalsData.gold.current.toFixed(2)}
              valueKg={metalsData.gold.currentKg.toFixed(2)}
              unit="€/oz"
              change={parseFloat(metalsData.gold.change)}
              changeLabel="vs hier"
              icon="🥇"
              trend={<TrendChart data={metalsData.gold.trend} color="#F59E0B" />}
            />
            <StatCard
              title="Argent (Silver)"
              value={metalsData.silver.current.toFixed(2)}
              valueKg={metalsData.silver.currentKg.toFixed(2)}
              unit="€/oz"
              change={parseFloat(metalsData.silver.change)}
              changeLabel="vs hier"
              icon="🥈"
              trend={<TrendChart data={metalsData.silver.trend} color="#9CA3AF" />}
            />
            <StatCard
              title="Platine (Platinum)"
              value={metalsData.platinum.current.toFixed(2)}
              valueKg={metalsData.platinum.currentKg.toFixed(2)}
              unit="€/oz"
              change={parseFloat(metalsData.platinum.change)}
              changeLabel="vs hier"
              icon="💎"
              trend={<TrendChart data={metalsData.platinum.trend} color="#60A5FA" />}
            />
            <StatCard
              title="Palladium"
              value={metalsData.palladium.current.toFixed(2)}
              valueKg={metalsData.palladium.currentKg.toFixed(2)}
              unit="€/oz"
              change={parseFloat(metalsData.palladium.change)}
              changeLabel="vs hier"
              icon="⚡"
              trend={<TrendChart data={metalsData.palladium.trend} color="#8B5CF6" />}
            />
          </div>
        </div>

        {/* API Metrics Section */}
        <div className="section-container">
          <ApiMetrics />
        </div>

        {/* Actualités Section */}
        <NewsWidget limit={3} />

        {/* Export CSV Section */}
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">
              <span className="section-icon">📥</span>
              Export CSV des cours
            </h2>
          </div>
          <div className="card glass-card">
            <div className="card-body">
              <div className="csv-export-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="metal-select" className="form-label">
                      <span className="label-icon">🥇</span>
                      Métal
                    </label>
                    <select
                      id="metal-select"
                      className="form-select modern-input"
                      value={csvExport.symbol}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, symbol: e.target.value }))}
                    >
                      <option value="XAU">🥇 Or (Gold)</option>
                      <option value="XAG">🥈 Argent (Silver)</option>
                      <option value="XPT">💎 Platine (Platinum)</option>
                      <option value="XPD">⚡ Palladium</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="start-date" className="form-label">
                      <span className="label-icon">📅</span>
                      Date de début
                    </label>
                    <input
                      type="date"
                      id="start-date"
                      className="form-input modern-input"
                      value={csvExport.startDate}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="end-date" className="form-label">
                      <span className="label-icon">📅</span>
                      Date de fin
                    </label>
                    <input
                      type="date"
                      id="end-date"
                      className="form-input modern-input"
                      value={csvExport.endDate}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="range-select" className="form-label">
                      <span className="label-icon">⏱️</span>
                      Intervalle
                    </label>
                    <select
                      id="range-select"
                      className="form-select modern-input"
                      value={csvExport.range}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, range: e.target.value }))}
                    >
                      <option value="raw">Données brutes</option>
                      <option value="hourly">Horaire</option>
                      <option value="daily">Journalier</option>
                      <option value="weekly">Hebdomadaire</option>
                      <option value="monthly">Mensuel</option>
                    </select>
                  </div>

                  <div className="form-group form-action">
                    <button
                      className="btn btn-primary modern-btn btn-export"
                      onClick={handleExportCSV}
                      disabled={csvExport.isExporting}
                    >
                      <span className="btn-icon">
                        {csvExport.isExporting ? '⏳' : '📥'}
                      </span>
                      {csvExport.isExporting ? 'Export en cours...' : 'Télécharger CSV'}
                    </button>
                  </div>
                </div>

                <div className="export-info">
                  <p className="info-text">
                    <span className="info-icon">ℹ️</span>
                    Le fichier CSV contiendra les colonnes : <strong>timestamp</strong> et <strong>mid</strong> (prix moyen)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="section-container">
          <h2 className="section-title">
            <span className="section-icon">⚡</span>
            Actions rapides
          </h2>
          <div className="quick-actions-grid">
            <div className="action-card glass-card" onClick={() => navigate('/charts')}>
              <div className="action-icon-wrapper">
                <div className="action-icon">📊</div>
              </div>
              <h3 className="action-title">Graphiques</h3>
              <p className="action-description">Créer et gérer vos graphiques personnalisés</p>
              <div className="action-arrow">→</div>
            </div>
            <div className="action-card glass-card" onClick={() => navigate('/products')}>
              <div className="action-icon-wrapper">
                <div className="action-icon">💰</div>
              </div>
              <h3 className="action-title">Produits</h3>
              <p className="action-description">Voir les produits et leurs cours</p>
              <div className="action-arrow">→</div>
            </div>
            <div className="action-card glass-card" onClick={() => navigate('/currencies')}>
              <div className="action-icon-wrapper">
                <div className="action-icon">💱</div>
              </div>
              <h3 className="action-title">Devises</h3>
              <p className="action-description">Consulter les taux de change</p>
              <div className="action-arrow">→</div>
            </div>
            <div className="action-card glass-card" onClick={() => navigate('/create-token')}>
              <div className="action-icon-wrapper">
                <div className="action-icon">🔑</div>
              </div>
              <h3 className="action-title">API Tokens</h3>
              <p className="action-description">Gérer vos clés d'accès API</p>
              <div className="action-arrow">→</div>
            </div>
          </div>
        </div>

        {/* Recent Updates Table */}
        <div className="card glass-card">
          <div className="card-header">
            <h2 className="card-title">
              <span className="card-icon">📋</span>
              Mises à jour récentes
            </h2>
            <span className="text-secondary">Dernières 4 heures</span>
          </div>
          <div className="card-body">
            <div className="table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Métal</th>
                    <th>Prix actuel</th>
                    <th>Dernière mise à jour</th>
                    <th>Tendance</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUpdates.map((update) => (
                    <tr key={update.id} className="table-row-hover">
                      <td>
                        <strong className="metal-name">{update.metal}</strong>
                      </td>
                      <td className="price-cell">{update.price?.toFixed(2)} €</td>
                      <td className="text-secondary">{update.time}</td>
                      <td>
                        <span
                          className={`badge modern-badge ${
                            update.status === 'up' ? 'badge-success' : 'badge-danger'
                          }`}
                        >
                          {update.status === 'up' ? '↑ Hausse' : '↓ Baisse'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
